import { emptyFilter, type GroupKey, type TaskFilter } from "./task-query";

/** 목록·보드가 같은 작업 표를 상태로 좁히는 범위. 기본은 미완료이고, 전체는 완료를 포함한다. */
export type TaskStatusScope = "all" | "open" | TaskFilter["statuses"][number];

const TASK_STATUS_VALUES = new Set<TaskFilter["statuses"][number]>(["unset", "notStarted", "inProgress", "blocked", "done"]);

export const TASK_STATUS_SCOPE_OPTIONS: Array<{ value: TaskStatusScope; label: string }> = [
  { value: "all", label: "전체" },
  { value: "open", label: "미완료" },
  { value: "notStarted", label: "시작 전" },
  { value: "inProgress", label: "진행 중" },
  { value: "blocked", label: "막힘" },
  { value: "done", label: "완료" },
  { value: "unset", label: "미정" },
];

function isStatusValue(value: string): value is TaskFilter["statuses"][number] {
  return TASK_STATUS_VALUES.has(value as TaskFilter["statuses"][number]);
}

/** 주소의 상태. 없으면 미완료. `scope=all`만 완료를 포함한 전체다. */
export function taskScopeFromParams(params: URLSearchParams): TaskStatusScope {
  const status = params.get("status");
  if (status && isStatusValue(status)) return status;
  if (params.get("scope") === "all") return "all";
  return "open";
}

/** 세그먼트에 보이는 범위. 상태가 하나면 그 값, 없고 완료를 숨기면 미완료, 아니면 전체. */
export function taskStatusScope(filter: TaskFilter): TaskStatusScope {
  const only = filter.statuses[0];
  if (filter.statuses.length === 1 && only && isStatusValue(only)) return only;
  if (filter.statuses.length === 0 && filter.hideDone) return "open";
  return "all";
}

/** 상태 범위는 세그먼트가 가진다. 미완료만 완료를 숨긴다. */
export function withStatusScope(filter: TaskFilter, scope: TaskStatusScope): TaskFilter {
  if (scope === "open") return { ...filter, statuses: [], hideDone: true };
  if (scope === "all") return { ...filter, statuses: [], hideDone: false };
  return { ...filter, statuses: [scope], hideDone: false };
}

/** 상태 범위를 주소에 남긴다. 미완료는 기본이라 파라미터를 두지 않는다. */
export function writeStatusScopeParam(params: URLSearchParams, scope: TaskStatusScope): void {
  params.delete("scope");
  params.delete("status");
  if (scope === "all") params.set("scope", "all");
  else if (scope !== "open") params.set("status", scope);
}

/** 작업 화면의 보기. `analytics`는 필터를 적용하지 않는 전체 요약이다. */
export type TaskView = "list" | "board" | "analytics";

export const LIST_GROUP_OPTIONS: GroupKey[] = ["none", "bucket", "status", "priority", "assignee", "scope", "scopeRoot", "due", "label"];
export const BOARD_GROUP_OPTIONS: GroupKey[] = ["bucket", "status", "priority", "assignee", "scope", "scopeRoot", "due"];

export function parseTaskView(params: URLSearchParams): TaskView {
  const v = params.get("view");
  return v === "board" || v === "analytics" ? v : "list";
}

export function parseTaskGroup(params: URLSearchParams, view: TaskView): GroupKey {
  const raw = (params.get("group") ?? params.get("by")) as GroupKey | null;
  const options = view === "board" ? BOARD_GROUP_OPTIONS : LIST_GROUP_OPTIONS;
  if (raw && options.includes(raw)) return raw;
  return "bucket";
}

export function groupForView(group: GroupKey, view: TaskView): GroupKey {
  if (view === "board" && !BOARD_GROUP_OPTIONS.includes(group)) return "bucket";
  return group;
}

/** Write view/group into the current tasks query, dropping the legacy `by` axis. */
export function writeTaskViewParams(params: URLSearchParams, view: TaskView, group: GroupKey): URLSearchParams {
  const next = new URLSearchParams(params);
  if (view === "list") next.delete("view");
  else next.set("view", view);
  if (group && group !== "bucket") next.set("group", group);
  else next.delete("group");
  next.delete("by");
  return next;
}

/** `?status=…&assignee=…` 같은 쿼리를 작업 필터로 읽는다. 분석 보기의 드릴스루와 딥링크가 함께 쓴다. */
export function filterFromParams(params: URLSearchParams): TaskFilter {
  const f = emptyFilter();
  const linked = params.get("linked");
  if (linked) f.linkedTaskIds = [linked];
  const assignee = params.get("assignee");
  if (assignee !== null) f.assignees = [assignee];
  const due = params.get("due");
  if (due === "overdue" || due === "today" || due === "week" || due === "none" || due === "scheduled") f.due = due;
  const status = params.get("status");
  if (status) f.statuses = [status as TaskFilter["statuses"][number]];
  const priority = params.get("priority");
  if (priority) f.priorities = [priority as TaskFilter["priorities"][number]];
  const bucket = params.get("bucket");
  if (bucket !== null) f.bucketIds = [bucket];
  const label = params.get("label");
  if (label) f.labelIds = [label];
  if (params.get("undecided") === "1") f.undecidedOnly = true;
  return f;
}
